import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Car, MessageCircle, Search, UserRound } from 'lucide-react';
import { ROTULO_STATUS, type VeiculoResumo } from '@erp/shared';
import { Dialog, Input, Skeleton } from '@erp/ui';
import { api } from '../lib/api';
import { telefoneLegivel } from '../lib/crm';
import { moeda } from '../lib/formato';
import { useSessao } from '../lib/sessao';

interface Resultado {
  id: string;
  para: string;
  titulo: string;
  detalhe: string;
}

const LIMITE = 5;
const MINIMO = 2;

/** Termo só vai para a API depois de uma pausa na digitação. */
function useTermoAdiado(valor: string, ms = 250) {
  const [adiado, setAdiado] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setAdiado(valor.trim()), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return adiado;
}

/**
 * Busca em tudo (Ctrl+K): carros, clientes e conversas.
 * Usa as mesmas rotas das telas, então cada papel só acha o que já poderia ver.
 * Cada fonte é independente: se uma falhar, as outras continuam respondendo.
 */
export function BuscaGlobal() {
  const { usuario, filial } = useSessao();
  const navegar = useNavigate();
  const [aberta, setAberta] = useState(false);
  const [texto, setTexto] = useState('');
  const termo = useTermoAdiado(texto);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setAberta(true);
      }
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, []);

  const recursos = usuario?.recursos ?? [];
  const crm = usuario?.papel !== 'financeiro' && recursos.includes('crm_basico');
  const conversas = crm && recursos.includes('central_atendimento');
  const ativa = aberta && termo.length >= MINIMO;
  const q = encodeURIComponent(termo);
  const chave = [filial, termo];

  const fontes: Fonte[] = [
    {
      nome: 'Carros',
      icone: <Car />,
      consulta: useFonte(['carros', ...chave], ativa, async () =>
        (await api<VeiculoResumo[]>(`/estoque/veiculos?busca=${q}`)).slice(0, LIMITE).map((v) => ({
          id: v.id,
          para: `/estoque/${v.id}`,
          titulo: `${v.marca} ${v.modelo} ${v.anoFabricacao}/${v.anoModelo}`,
          detalhe: [v.placa, ROTULO_STATUS[v.status], v.precoCentavos && moeda(v.precoCentavos)]
            .filter(Boolean)
            .join(' · '),
        })),
      ),
    },
    {
      nome: 'Clientes',
      icone: <UserRound />,
      visivel: crm,
      consulta: useFonte(['clientes', ...chave], ativa && crm, async () =>
        (await api<ClienteAchado[]>(`/clientes?busca=${q}`)).slice(0, LIMITE).map((c) => ({
          id: c.id,
          para: `/clientes/${c.id}`,
          titulo: c.nome,
          detalhe: telefoneLegivel(c.telefone) ?? '',
        })),
      ),
    },
    {
      nome: 'Conversas',
      icone: <MessageCircle />,
      visivel: conversas,
      consulta: useFonte(['conversas', ...chave], ativa && conversas, async () =>
        (await api<ConversaAchada[]>(`/atendimento/conversas?status=todas&busca=${q}`))
          .slice(0, LIMITE)
          .map((c) => ({
            id: c.id,
            para: `/atendimento/${c.id}`,
            titulo: c.cliente?.nome ?? 'Conversa',
            detalhe: c.ultima?.texto ?? '',
          })),
      ),
    },
  ];
  const visiveis = fontes.filter((f) => f.visivel !== false);
  const primeiro = visiveis.flatMap((f) => f.consulta.data ?? [])[0];

  const fechar = () => {
    setAberta(false);
    setTexto('');
  };

  if (!usuario) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setAberta(true)}
        className="flex size-9 items-center justify-center gap-2 rounded-md text-sm text-muted-foreground hover:bg-accent md:w-full md:justify-start md:border md:bg-background md:px-3"
        aria-keyshortcuts="Control+K Meta+K"
        aria-label="Buscar placa, cliente ou telefone"
      >
        <Search className="size-5 md:size-4" aria-hidden />
        <span className="hidden flex-1 truncate whitespace-nowrap text-left md:inline">
          Buscar placa, cliente ou telefone
        </span>
        <kbd className="hidden rounded border px-1.5 text-xs lg:inline">Ctrl K</kbd>
      </button>
      <Dialog
        aberto={aberta}
        aoFechar={fechar}
        titulo="Buscar"
        descricao="Carros, clientes e conversas da loja"
      >
        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            if (!primeiro) return;
            fechar();
            navegar(primeiro.para);
          }}
        >
          <label className="relative block">
            <span className="sr-only">Buscar</span>
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              type="search"
              autoFocus
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Placa, modelo, nome ou telefone"
              className="pl-9"
            />
          </label>
        </form>
        <div className="mt-4 flex flex-col gap-4" aria-live="polite">
          {texto.trim().length < MINIMO ? (
            <p className="text-sm text-muted-foreground">Digite pelo menos 2 letras ou números.</p>
          ) : (
            visiveis.map((f) => <Grupo key={f.nome} fonte={f} aoEscolher={fechar} />)
          )}
          {ativa && visiveis.every((f) => f.consulta.isSuccess && f.consulta.data.length === 0) && (
            <p className="text-sm text-muted-foreground">Nada encontrado para “{termo}”.</p>
          )}
        </div>
      </Dialog>
    </>
  );
}

interface ClienteAchado {
  id: string;
  nome: string;
  telefone: string | null;
}

interface ConversaAchada {
  id: string;
  cliente: { nome: string } | null;
  ultima: { texto: string } | null;
}

type Consulta = ReturnType<typeof useFonte>;

function useFonte(chave: string[], habilitada: boolean, buscar: () => Promise<Resultado[]>) {
  return useQuery({ queryKey: ['busca', ...chave], enabled: habilitada, queryFn: buscar });
}

interface Fonte {
  nome: string;
  icone: ReactNode;
  visivel?: boolean;
  consulta: Consulta;
}

function Grupo({ fonte, aoEscolher }: { fonte: Fonte; aoEscolher: () => void }) {
  const { consulta: c } = fonte;
  if (c.isError)
    return (
      <p className="text-sm text-destructive">
        Não foi possível buscar {fonte.nome.toLowerCase()} agora.
      </p>
    );
  if (c.isPending) return <Skeleton className="h-10" />;
  if (!c.data?.length) return null;
  return (
    <section role="group" aria-label={fonte.nome}>
      <h3 className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground [&_svg]:size-3.5">
        {fonte.icone}
        {fonte.nome}
      </h3>
      <ul className="flex flex-col">
        {c.data.map((r) => (
          <li key={r.id}>
            <Link
              to={r.para}
              onClick={aoEscolher}
              className="flex flex-col rounded-md px-2 py-2 hover:bg-accent focus-visible:bg-accent"
            >
              <span className="font-medium">{r.titulo}</span>
              {r.detalhe && (
                <span className="truncate text-sm text-muted-foreground">{r.detalhe}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
